/* eslint-disable react-hooks/exhaustive-deps */
import { payrollApi } from 'Api/payroll';
import useDebounce from 'components/Global/Hooks/UseDebounce';
import dayjs, { Dayjs } from 'dayjs';
import { TablePagination, TableSorter } from 'interfaces/global.interface';
import { useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import PaymentTableHeader from './Table/Header';
import PaymentTable from './Table/PaymentTable';

const PaymentComponent = () => {
	const [paymentData, setPaymentData] = useState<any[]>([]);
	const [totalRecords, setTotalRecords] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(30);
	const [sortField, setSortField] = useState('');
	const [sortOrder, setSortOrder] = useState('');
	const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
		dayjs().startOf('month'),
		dayjs().endOf('month'),
	]);
	const [searchText, setSearchText] = useState('');
	const debouncedSearchText = useDebounce(searchText, 500);
	const [filterOptions, setFilterOptions] = useState('');
	const [bankType, setBankType] = useState('STANBIC');

	const handleSearch = (value: string) => {
		setSearchText(value);
		setCurrentPage(1);
	};

	const handleSearchApi = (value: string) => {
		setSearchText(value);
		setCurrentPage(1);
	};

	const handleDateRangeChange = (
		value: [Dayjs, Dayjs] | null
	) => {
		if (value && value[0] && value[1]) {
			setDateRange(value);
		} else {
			setDateRange([
				dayjs().startOf('month'),
				dayjs().endOf('month'),
			]);
		}
		setCurrentPage(1);
	};

	const handleBankType = (value: string) => {
		setPageSize(20);
		setCurrentPage(1);
		setBankType(value);
	};

	const handleStatusFilter = (value: string) => {
		setPageSize(20);
		setCurrentPage(1);
		setFilterOptions(value);
	};

	const tableChangeHandler = (
		pagination: TablePagination,
		filters: any,
		sorter: TableSorter
	) => {
		setTotalRecords(pagination.total);
		setCurrentPage(pagination.current);
		setPageSize(pagination.pageSize);
		setSortOrder(
			sorter?.order === 'ascend' ? 'asc' : sorter?.order ? 'desc' : 'desc'
		);
		setSortField(sorter.field);
	};

	const fetchPayments = async () => {
		try {
			const query: Record<string, unknown> = {
				page: currentPage,
				sortBy: sortField,
				sortOrder: sortOrder,
				pageSize: pageSize,
				status: filterOptions,
				bankType,
				startDate: dateRange[0].format('YYYY-MM-DD'),
				endDate: dateRange[1].format('YYYY-MM-DD'),
			};
			if (debouncedSearchText.trim()) {
				query.search = debouncedSearchText.trim();
			}
			setIsLoading(true);
			const loanRequest = await payrollApi.getPaymentLogs(query);

			setPaymentData(loanRequest.data.data);
			setTotalRecords(loanRequest.data.total);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching Payments';
			toastText(errorMessage, 'error');
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchPayments();
	}, [
		currentPage,
		filterOptions,
		sortField,
		sortOrder,
		pageSize,
		bankType,
		debouncedSearchText,
		dateRange,
	]);

	return (
		<>
			<PaymentTableHeader
				searchText={searchText}
				handleSearch={handleSearch}
				handleSearchApi={handleSearchApi}
				dateRange={dateRange}
				onDateRangeChange={handleDateRangeChange}
				filterOptions={filterOptions}
				handleStatusFilter={handleStatusFilter}
				handleBankType={handleBankType}
				bankType={bankType}
			/>

			<div>
				<PaymentTable
					tableData={paymentData}
					totalRecords={totalRecords}
					currentPage={currentPage}
					pageSize={pageSize}
					fetchRecords={fetchPayments}
					tableChangeHandler={tableChangeHandler}
					isLoading={isLoading}
					bankType={bankType}
				/>
			</div>
		</>
	);
};

export default PaymentComponent;
